/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useContext, useMemo } from 'react';

import exportWithBoundary from '../../../../helpers/exportWithBoundary';
import useTranslations from '../../../../hooks/useTranslations';
import Pagination from '../../../Pagination';
import { JobMapContext } from '../JobMapContext';
import JobListItem from '../JobMapJobListItem';
import JobMapJobMedia from '../JobMapJobMedia';
import { closeIcon } from '../icons';
import useJobMapJobPostings from '../useJobMapJobPostings';
import {
  JobLocationAddress,
  JobLocationClear,
  JobLocationContainer,
  JobLocationCount,
  JobLocationHeader,
  JobLocationResults,
} from './styles';

const t = useTranslations('JobMapJobLocation');

function JobMapJobLocation() {
  const {
    currentPlace,
    jobMapId,
    params,
    primaryColor,
    setCurrentJob,
    setCurrentPlace,
    setParams,
  } = useContext(JobMapContext);

  if (currentPlace === null) return <JobLocationContainer />;

  const { data: jobData, isLoading: jobsLoading } = useJobMapJobPostings(
    jobMapId,
    {
      ...params,
      ...(currentPlace ? { geohash: currentPlace.geohash } : {}),
    },
    {
      onSuccess: (data) => {
        if (data.meta.total_count === 1) {
          setCurrentJob(data.entries[0]);
          setCurrentPlace(null);
        }
      },
    }
  );

  const jobLocation = useCallback((result) => {
    if (!result || result.locations.length === 0) return null;
    const addressParts = [];
    const loc = result.locations[0];
    if (loc.street_address) addressParts.push(`${loc.street_address},`);
    addressParts.push(loc.canonical_name);
    if (loc.postal_code) addressParts.push(loc.postal_code);
    if (
      loc.country_code !== 'US' &&
      loc.country_code.indexOf('United States') < 0
    )
      addressParts.push(loc.country_code);
    return addressParts.join(' ');
  }, []);

  const setPage = useCallback(
    (newPage) => {
      setParams((prev) => ({
        ...prev,
        page: newPage,
      }));
    },
    [setParams]
  );

  const clearLocation = useCallback(() => {
    setCurrentPlace(null);
  }, [setCurrentPlace]);

  const firstJob = useMemo(() => {
    if (!jobData) return null;

    return jobData.entries[0];
  }, [jobData]);

  return (
    <JobLocationContainer>
      {firstJob && <JobMapJobMedia jobPosting={firstJob} />}
      <JobLocationHeader>
        <JobLocationCount>
          {jobData &&
            jobData.meta &&
            jobData.meta.total_count > 1 &&
            t('job_maps.openings', { count: jobData.meta.total_count })}
        </JobLocationCount>
        <JobLocationClear size={24} onClick={clearLocation}>
          {closeIcon}
        </JobLocationClear>
        {jobData && jobLocation(jobData.entries[0]) && (
          <JobLocationAddress>
            {jobLocation(jobData.entries[0])}
          </JobLocationAddress>
        )}
      </JobLocationHeader>
      <JobLocationResults primaryColor={primaryColor}>
        {!jobsLoading &&
          jobData.entries.map((job) => (
            <JobListItem key={job.id} jobPosting={job} />
          ))}
        {jobData && jobData.meta && (
          <Pagination
            total={jobData.meta.total_count}
            page={params.page}
            perPage={params.per_page}
            setPage={setPage}
          />
        )}
      </JobLocationResults>
    </JobLocationContainer>
  );
}

function JobMapJobLocationFallback() {
  return <JobLocationContainer />;
}

export default exportWithBoundary(JobMapJobLocation, JobMapJobLocationFallback);
